import React, { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import Tippy from '@tippyjs/react'
import { Card, InfoBox, ViewHeader, Submit, Input, TextArea, useGet, useSubmit, Help } from '@enkla-gui'
import { icons } from '@config'


export default function Contact() {
  const { type, id } = useParams()
  const { data: recipients } = useGet({ endpoint: `api/contact/recipients/${type}/${id}` })
  const [excludedTenantIds, setExcludedTenantIds] = useState([])
  const [filter, setFilter] = useState('')

  const { register, post, loading, error, setValue, handleSubmit, watch } = useSubmit({ 
    endpoint: `api/contact/${type}/${id}`, 
    success: () => successCallback() 
  })

  const bodyValue = watch('body') || ''
  const subjectValue = watch('subject') || ''

  useEffect(() => {
    if (recipients?.tenants) {
      setExcludedTenantIds([])
    }
  }, [recipients])

  const successCallback = () => {
    window.location.hash = '#!/contact/messages'
  }

  const handleFormSubmit = handleSubmit((data) => {
    post({
      ...data,
      excluded_tenant_ids: excludedTenantIds
    })
  })

  const toggleTenantExclusion = (tenantId) => {
    setExcludedTenantIds(prev => 
      prev.includes(tenantId)
        ? prev.filter(id => id !== tenantId)
        : [...prev, tenantId]
    )
  }

  const selectAll = () => setExcludedTenantIds([])
  
  const deselectAll = () => {
    if (recipients?.tenants) {
      setExcludedTenantIds(recipients.tenants.map(t => t.id))
    }
  }

  const filteredTenants = useMemo(() => {
    if (!recipients?.tenants) return []
    if (!filter.trim()) return recipients.tenants
    const lowerFilter = filter.toLowerCase()
    return recipients.tenants.filter(t => 
      t.name?.toLowerCase().includes(lowerFilter) || 
      t.email?.toLowerCase().includes(lowerFilter)
    )
  }, [recipients?.tenants, filter])

  const includedCount = (recipients?.tenants?.length || 0) - excludedTenantIds.length
  const totalCount = recipients?.tenants?.length || 0
  const exampleTenant = recipients?.tenants?.[0]
  const entityName = exampleTenant?.entity_name || ''
  const entityAddress = exampleTenant?.entity_address || ''
  
  const hasPlaceholder = /\[(namn|objekt|adress)\]/i.test(bodyValue) || /\[(namn|objekt|adress)\]/i.test(subjectValue)

  const breadcrumbs = useMemo(() => {
    if (!recipients?.breadcrumb) return []
    return [...recipients.breadcrumb, { title: `Kontakta hyresgäst${type !== 'tenant' ? 'er' : ''}`, icon: icons.email, link: `#!/contact/${type}/${id}` }]
  }, [recipients?.breadcrumb])
  
  const replacePlaceholders = (text) => {
    return text
      .replace(/\[namn\]/gi, exampleTenant?.name || '')
      .replace(/\[objekt\]/gi, entityName)
      .replace(/\[adress\]/gi, entityAddress)
  }

  // Show warning if no tenants exist at all
  if (recipients && totalCount === 0) {
    return (
      <>
        <ViewHeader 
          title='Kontakta hyresgäster' 
          icon={icons.email} 
          breadcrumbs={breadcrumbs}
        />
        <Help type='warning' title='Inga hyresgäster hittades'>
          <p>Det finns inga hyresgäster kopplade till detta {type === 'entity' ? 'objekt' : type === 'company' ? 'företag' : 'objekt'}. 
          Lägg till hyresgäster genom att skapa uthyrningar först.</p>
        </Help>
      </>
    )
  }

  return (
    <>
      <ViewHeader 
        loading={!recipients} 
        title='Kontakta hyresgäster' 
        icon={icons.email} 
        breadcrumbs={breadcrumbs}
      />
      <div class='row'>
        <Card
          loading={!recipients}
          cols='col-8'
          buttons={{
            footer: [<Submit onSubmit={handleFormSubmit} text='Skicka' sending={loading} disabled={includedCount === 0} />]
          }}>
          <Input controls={{ register }} name='subject' options={{ required: true }} label='Ämnesrad' error={error} />
          <TextArea controls={{ register }} name='body' options={{ required: true }} label='Innehåll' error={error} />
          
          <div class='mt-3 p-3' style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <small class='text-muted d-block mb-2'>
              <i class='fas fa-info-circle' /> Tillgängliga platshållare:{' '}
              <Tippy content={exampleTenant ? `Exempel: "${exampleTenant.name}"` : 'Ersätts med hyresgästens namn'}>
                <code style={{ backgroundColor: '#e7f3ff', padding: '2px 6px', borderRadius: '3px', cursor: 'help', marginRight: '4px' }}>[namn]</code>
              </Tippy>
              <Tippy content={entityName ? `Exempel: "${entityName}"` : 'Ersätts med objektets namn'}>
                <code style={{ backgroundColor: '#e7f3ff', padding: '2px 6px', borderRadius: '3px', cursor: 'help', marginRight: '4px' }}>[objekt]</code>
              </Tippy>
              <Tippy content={entityAddress ? `Exempel: "${entityAddress}"` : 'Ersätts med objektets adress'}>
                <code style={{ backgroundColor: '#e7f3ff', padding: '2px 6px', borderRadius: '3px', cursor: 'help' }}>[adress]</code>
              </Tippy>
            </small>
            {hasPlaceholder && (
              <div class='mt-2'>
                <small class='text-muted'>Förhandsvisning{exampleTenant ? ` för ${exampleTenant.name}` : ''}:</small>
                <div class='mt-1 p-2' style={{ backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '13px' }}>
                  {subjectValue && (
                    <div><strong>Ämne:</strong> {replacePlaceholders(subjectValue)}</div>
                  )}
                  {bodyValue && (
                    <div class='mt-1' style={{ whiteSpace: 'pre-wrap' }}>{replacePlaceholders(bodyValue)}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
        <div class='col-4'>
        <div class='row'>
            <InfoBox 
              loading={!recipients} 
              title='Mottagare' 
              subtitle={`${includedCount} av ${totalCount}`}
              icon={icons.tenant}
              status={includedCount === 0 ? 'warning' : 'default'}
            />
          </div>
          <Card 
            title={`Välj mottagare`}
            icon={icons.tenant}
            loading={!recipients}
            collapsed={false}
          >
            {recipients?.tenants && recipients.tenants.length > 0 ? (
              <>
                <div class='d-flex gap-2 mb-3'>
                  <button class='btn btn-sm btn-outline-primary' onClick={selectAll}>
                    <i class='fas fa-check-square' /> Markera alla
                  </button>
                  <button class='btn btn-sm btn-outline-secondary' onClick={deselectAll}>
                    <i class='fas fa-square' /> Avmarkera alla
                  </button>
                </div>
                <div class='component_Input'>
                  <div class='form-group'>
                    <label>Filtrera hyresgäster</label>
                    <input
                      type='text'
                      class='form-control'
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  {filteredTenants.length > 0 ? (
                    filteredTenants.map((tenant) => (
                      <div 
                        key={tenant.id} 
                        class={`d-flex align-items-center py-2 px-1 ${excludedTenantIds.includes(tenant.id) ? 'text-muted' : ''}`}
                        style={{ borderBottom: '1px solid #eee' }}
                      >
                        <div class='form-check mb-0'>
                          <input
                            class='form-check-input'
                            type='checkbox'
                            id={`tenant-${tenant.id}`}
                            checked={!excludedTenantIds.includes(tenant.id)}
                            onChange={() => toggleTenantExclusion(tenant.id)}
                          />
                        </div>
                        <label 
                          htmlFor={`tenant-${tenant.id}`} 
                          class='mb-0 ms-2 flex-grow-1' 
                          style={{ cursor: 'pointer', textDecoration: excludedTenantIds.includes(tenant.id) ? 'line-through' : 'none' }}
                        >
                          <div>{tenant.name}</div>
                          <small class='text-muted'>{tenant.email}</small>
                        </label>
                      </div>
                    ))
                  ) : (
                    <div class='mt-2'>
                      <Help type='info' title={null}>
                        <p>Inga hyresgäster matchar sökningen.</p>
                      </Help>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p class='text-muted mb-0'>Inga hyresgäster hittades.</p>
            )}
          </Card>
        </div>
      </div>
    </>
  )
}
